import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Button, Form, InputGroup } from 'react-bootstrap';
import './MediaPricelist.css';
import api from '../services/api';
import useColumnWidths from '../components/useColumnWidths';
import ColumnWidthsReset from '../components/ColumnWidthsReset';

/** Sloupce výpisu v pořadí hlavičky — klíče, pod kterými se ukládají šířky. */
const SLOUPCE = ['medium', 'typ', 'vydavatel', 'projekty', 'ceniky', 'aktivniCenik', 'stav', 'akce'];

const ITEMS_PER_PAGE = 100;

function MediaPricelist({ openTab }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('available');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [media, setMedia] = useState([]);
  const [types, setTypes] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);

  // Šířky sloupců tažením — ukládají se uživateli do DB (migrace 048).
  const { wrapRef, colgroup, tableStyle, resizerProps, maUlozene, vychozi } =
    useColumnWidths('media.list', SLOUPCE);

  // Fetch media from API
  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getMedia({
        search: debouncedSearch,
        type: typeFilter,
        status: statusFilter,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        sort: sortColumn,
        order: sortOrder,
      });

      setMedia(result.data || []);
      setTypes(result.types || []);
      const pagination = result.pagination || {};
      setTotalPages(pagination.totalPages || 1);
      setTotalItems(pagination.total || 0);
    } catch (err) {
      console.error('Failed to fetch media:', err);
      setMedia([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, typeFilter, statusFilter, currentPage, sortColumn, sortOrder]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const safePage = Math.min(currentPage, totalPages || 1);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <i className="bi bi-chevron-expand text-muted ms-1" style={{ fontSize: '0.7rem' }}></i>;
    return sortOrder === 'asc'
      ? <i className="bi bi-chevron-up ms-1" style={{ fontSize: '0.7rem' }}></i>
      : <i className="bi bi-chevron-down ms-1" style={{ fontSize: '0.7rem' }}></i>;
  };

  const handleOpenDetail = (medium) => {
    openTab(
      `media-detail-${medium.id}`,
      medium.name,
      `/media-pricelist/${medium.id}`,
      'MediaPricelistDetail',
      { mediaId: medium.id }
    );
  };

  const handleAddMedium = () => {
    openTab('media-new', 'Nové médium', '/media/new', 'MediaForm');
  };

  const handleEditMedium = (medium) => {
    openTab(
      `media-edit-${medium.id}`,
      `Upravit: ${medium.name}`,
      `/media/${medium.id}/edit`,
      'MediaForm',
      { mediaId: medium.id }
    );
  };

  const handleTypeChange = (e) => {
    setTypeFilter(e.target.value);
    setCurrentPage(1);
  };
  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };
  const handleReset = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setTypeFilter('');
    setStatusFilter('');
    setSortColumn('name');
    setSortOrder('asc');
    setCurrentPage(1);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (safePage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (safePage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = safePage - 1; i <= safePage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const startIdx = (safePage - 1) * ITEMS_PER_PAGE;

  return (
    <Container fluid className="px-4">
      {/* Header Bar */}
      <div className="header-bar mb-4">
        <Container fluid className="px-4">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h4 className="mb-0"><i className="bi bi-newspaper me-2"></i>Média a ceníky</h4>
            </div>
            <div className="col-md-6 text-end">
              <Button variant="primary" size="sm" onClick={handleAddMedium}>
                <i className="bi bi-plus-lg"></i> Přidat nové
              </Button>
            </div>
          </div>
        </Container>
      </div>

      {/* Filters and Table */}
      <div className="table-container">
        <div className="row g-3 align-items-end mb-4">
          <div className="col-md-4">
            <label className="form-label">Hledat</label>
            <InputGroup>
              <span className="input-group-text bg-white border-end-0">
                <i className="bi bi-search"></i>
              </span>
              <Form.Control
                className="border-start-0 search-box"
                placeholder="Médium, vydavatel, ID…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>
          <div className="col-md-3">
            <label className="form-label">Typ média</label>
            <Form.Select value={typeFilter} onChange={handleTypeChange}>
              <option value="">Typ média (vše)</option>
              {types.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </Form.Select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Stav</label>
            <Form.Select value={statusFilter} onChange={handleStatusChange}>
              <option value="">Stav (vše)</option>
              <option value="available">Aktivní</option>
              <option value="unavailable">Neaktivní</option>
            </Form.Select>
          </div>
          <div className="col-md-3">
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" className="flex-grow-1" onClick={handleReset}>
                <i className="bi bi-arrow-counterclockwise"></i> Reset
              </Button>
              <ColumnWidthsReset maUlozene={maUlozene} vychozi={vychozi} />
            </div>
          </div>
        </div>

        {/* Table with loading overlay */}
        <div className="table-loading-wrapper" style={{ position: 'relative' }}>
          {loading && (
            <div className="table-loading-overlay">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Načítání...</span>
              </div>
            </div>
          )}
          <div className="table-responsive grid-wrap" ref={wrapRef} style={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.2s ease', pointerEvents: loading ? 'none' : 'auto' }}>
            <Table hover className="align-middle grid-resizable" style={tableStyle}>
              {colgroup && (
                <colgroup>
                  {colgroup.map(c => <col key={c.key} style={{ width: c.width }} />)}
                </colgroup>
              )}
              <thead className="table-light">
                <tr>
                  <th role="button" onClick={() => handleSort('name')}>Médium <SortIcon column="name" /><span {...resizerProps('medium')} /></th>
                  <th role="button" onClick={() => handleSort('type')}>Typ <SortIcon column="type" /><span {...resizerProps('typ')} /></th>
                  <th role="button" onClick={() => handleSort('publisher_name')}>Vydavatel <SortIcon column="publisher_name" /><span {...resizerProps('vydavatel')} /></th>
                  <th role="button" className="text-center" onClick={() => handleSort('project_count')}>Projekty <SortIcon column="project_count" /><span {...resizerProps('projekty')} /></th>
                  <th role="button" className="text-center" onClick={() => handleSort('ratecard_count')}>Ceníky <SortIcon column="ratecard_count" /><span {...resizerProps('ceniky')} /></th>
                  <th role="button" onClick={() => handleSort('active_ratecard_name')}>Aktivní ceník <SortIcon column="active_ratecard_name" /><span {...resizerProps('aktivniCenik')} /></th>
                  <th role="button" className="text-center" onClick={() => handleSort('is_available')}>Stav <SortIcon column="is_available" /><span {...resizerProps('stav')} /></th>
                  <th className="text-end">Akce<span {...resizerProps('akce')} /></th>
                </tr>
              </thead>
              <tbody>
                {media.map(medium => (
                  <tr key={medium.id}>
                    <td>
                      <div className="company-name">
                        <a href="#" onClick={(e) => { e.preventDefault(); handleOpenDetail(medium); }} className="text-decoration-none text-dark">
                          {medium.name}
                        </a>
                      </div>
                      <div className="company-details">ID: {medium.external_id || medium.id}</div>
                    </td>
                    <td>{medium.type_label || medium.type || '—'}</td>
                    <td>{medium.publisher_name || '—'}</td>
                    <td className="text-center">{medium.project_count}</td>
                    <td className="text-center">
                      {medium.active_ratecard_count}/{medium.ratecard_count}
                    </td>
                    <td>
                      {medium.active_ratecard_name && medium.active_ratecard_id ? (
                        <span
                          className="badge bg-primary"
                          role="button"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            openTab(
                              `pricelist-items-${medium.active_ratecard_id}`,
                              `Ceník: ${medium.active_ratecard_name}`,
                              `/media-pricelist/${medium.id}/pricelist/${medium.active_ratecard_id}`,
                              'PricelistItems',
                              {
                                pricelistId: String(medium.active_ratecard_id),
                                pricelistName: medium.active_ratecard_name,
                                mediaName: medium.name,
                                mediaId: medium.id,
                              }
                            );
                          }}
                        >
                          {medium.active_ratecard_name}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center">
                      {medium.is_available ? (
                        <span className="badge bg-success">Aktivní</span>
                      ) : (
                        <span className="badge bg-secondary">Neaktivní</span>
                      )}
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-secondary"
                        className="btn-action me-1"
                        title="Upravit médium"
                        onClick={() => handleEditMedium(medium)}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button
                        variant="outline-secondary"
                        className="btn-action"
                        title="Otevřít detail"
                        onClick={() => handleOpenDetail(medium)}
                      >
                        <i className="bi bi-eye"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
                {media.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      Žádná média neodpovídají filtru
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div>
            <span className="text-muted">
              Zobrazeno: {media.length > 0 ? startIdx + 1 : 0}–{startIdx + media.length} z {totalItems}
            </span>
          </div>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${safePage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(1)} disabled={safePage === 1}>«</button>
              </li>
              <li className={`page-item ${safePage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={safePage === 1}>‹</button>
              </li>
              {getPageNumbers().map((page, idx) => (
                <li key={idx} className={`page-item ${page === safePage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}>
                  {page === '...' ? (
                    <span className="page-link">...</span>
                  ) : (
                    <button className="page-link" onClick={() => setCurrentPage(page)}>{page}</button>
                  )}
                </li>
              ))}
              <li className={`page-item ${safePage >= totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={safePage >= totalPages}>›</button>
              </li>
              <li className={`page-item ${safePage >= totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(totalPages)} disabled={safePage >= totalPages}>»</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </Container>
  );
}

export default MediaPricelist;
